import { RolePermission } from 'src/app/models/rolePermission.model';

/**
 * Role-level helpers for the Role Permissions screen (DASH-3179).
 *
 * These sit alongside the verb (`role-permission-verb.util`) and grouping
 * (`role-permission-grouping.util`) helpers shipped in part 1 of this rebuild.
 */

/**
 * Names of the seeded system roles. The backend protects these by a string match on the role name
 * (design-spec §3.2/§3.3) — there is no `isSystemRole` flag on the DTO yet (backend ask BE-3). Until
 * that flag exists the frontend re-derives the badge the same way the backend gates the roles, so the
 * two agree. Replace this with the DTO flag once BE-3 ships (design-spec §13).
 */
export const SYSTEM_ROLE_NAMES: readonly string[] = ['Admin', 'User'];

/** True when a role is one of the protected seeded system roles (case-insensitive, trimmed). */
export function isSystemRole(name: string | null | undefined): boolean {
  const normalised = name?.trim().toLowerCase();
  return !!normalised && SYSTEM_ROLE_NAMES.some(systemName => systemName.toLowerCase() === normalised);
}

/**
 * Suffix of the auto-provisioned name backend integration accounts get their role seeded with
 * (`IntegrationService.GetOrCreateIntegrationRoleAsync`: `"{IntegrationType} Integration Service"`).
 * `UserRoleDto` does not expose `UserRole.IntegrationType` — there is no flag on the wire to key off
 * — so this re-derives the same signal the backend rejection (`RequireMfa` on an integration role)
 * is keyed on. It is a best-effort stand-in: an admin renaming/re-describing the auto-created role
 * defeats it, same caveat as {@link isSystemRole}. Replace with a DTO flag once one ships.
 */
const INTEGRATION_ROLE_NAME_SUFFIX = 'integration service';

/** True when a role's name matches the auto-provisioned integration-service naming convention. */
export function isIntegrationRoleName(name: string | null | undefined): boolean {
  const normalised = name?.trim().toLowerCase();
  return !!normalised && normalised.endsWith(INTEGRATION_ROLE_NAME_SUFFIX);
}

/**
 * Count of objects a role grants at least one verb on, for the rail row and the detail metadata
 * (design-spec §5.5). "Granted" means a non-zero mask; a persisted row at `Permissions = 0`
 * (revoke ≠ delete, design-spec §8) counts as not granted.
 */
export function countGrantedRolePermissions(rolePermissions: RolePermission[] | null | undefined): number {
  return (rolePermissions ?? []).filter(rolePermission => (rolePermission.permissions ?? 0) !== 0).length;
}
